import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Clock3, Heart, ListChecks, Package, Plus, Trash2, Users } from "lucide-react";
import { Button, IconButton, Input, Switch, Textarea } from "@akuna/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Autocomplete, MultipleAutocomplete } from "@/components/ui/autocomplete";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { AlertColorPicker } from "@/pages/daycare-hotel/reservation/AlertColorPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { getPetStayNotesLabelKey } from "@/pages/daycare-hotel/reservation/petStayNotesLabel";
import {
  petAlertColorPresentation,
  type PetRelationshipKind,
} from "@/pages/daycare-hotel/reservation/petProfileModel";
import { draftTempId, type PetDaycareHotelDraft } from "./petDaycareHotelDraft";
import {
  useDaycareHotelPetSearch,
  useDaycareHotelPetsByIds,
  usePetDaycareHotelInitialDraft,
  type DaycareHotelPetOption,
} from "./usePetDaycareHotelDraft";
import { useStayActivityTypes } from "@/pages/daycare-hotel/activities/useStayActivityTypes";
import { colorDotClass, colorDotStyle } from "@/pages/daycare-hotel/accommodations/colors";
import { useStayPacks } from "@/hooks/useStayPacks";

export type PetDaycareHotelFieldsProps = {
  petId?: string | null;
  value: PetDaycareHotelDraft;
  onChange: (draft: PetDaycareHotelDraft) => void;
  disabled?: boolean;
};

const RELATIONSHIP_KINDS: PetRelationshipKind[] = ["likes", "dislikes", "neutral"];

const RELATIONSHIP_DOT_CLASS: Record<PetRelationshipKind, string> = {
  likes: "bg-state-success",
  dislikes: "bg-state-danger",
  neutral: "bg-muted-foreground",
};

function SectionHeader({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof AlertTriangle;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="space-y-0.5">
        <Label className="text-sm font-medium">{title}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function EmptyState({ children }: { children: string }) {
  return (
    <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
      {children}
    </p>
  );
}

function RowShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-akuna-xs transition-colors hover:border-primary/30">
      {children}
    </div>
  );
}

function PetOptionRow({ option }: { option: DaycareHotelPetOption }) {
  const { t } = useTranslation("daycareHotel");
  return (
    <div className="flex w-full min-w-0 items-center gap-3">
      <Avatar className="h-9 w-9 shrink-0">
        {option.petPhotoPath ? <AvatarImage src={option.petPhotoPath} alt={option.petName} /> : null}
        <AvatarFallback className="bg-muted/50 text-muted-foreground">
          <CustomIcons name="paws" className="h-4 w-4 text-current" />
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground group-hover:text-primary">
          {option.petName}
          {option.petRecordNumber ? (
            <span className="ml-1.5 text-muted-foreground group-hover:text-primary">
              #{option.petRecordNumber}
            </span>
          ) : null}
        </p>
        {option.ownerName ? (
          <p className="truncate text-xs text-muted-foreground group-hover:text-primary">
            {t("petForm.ownerPrefix")}: {option.ownerName}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function RelationshipPetPicker({
  petId,
  selectedPet,
  disabled,
  onSelect,
}: {
  petId?: string | null;
  selectedPet: DaycareHotelPetOption | null;
  disabled?: boolean;
  onSelect: (option: DaycareHotelPetOption) => void;
}) {
  const { t } = useTranslation(["daycareHotel", "common"]);
  const [search, setSearch] = useState(selectedPet?.label ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data: options, isFetching } = useDaycareHotelPetSearch(debouncedSearch, petId);
  const syncedLabel = useRef<string | null>(null);

  // Quando o pet já vinculado é resolvido, mostra o rótulo completo no campo.
  useEffect(() => {
    if (!selectedPet || syncedLabel.current === selectedPet.label) return;
    syncedLabel.current = selectedPet.label;
    setSearch(selectedPet.label);
  }, [selectedPet]);

  const searchOptions = options ?? [];
  const listOptions: DaycareHotelPetOption[] =
    selectedPet && !searchOptions.some((option) => option.value === selectedPet.value)
      ? [selectedPet, ...searchOptions]
      : searchOptions;

  return (
    <Autocomplete
      value={search}
      onChange={setSearch}
      onSelect={(option) => {
        const picked = option as DaycareHotelPetOption;
        syncedLabel.current = picked.label;
        setSearch(picked.label);
        onSelect(picked);
      }}
      options={listOptions}
      selectedValue={selectedPet?.value}
      disableLocalFilter
      disabled={disabled}
      popoverPortalled={false}
      openOnFocus
      ariaLabel={t("daycareHotel:petForm.relationshipPetPlaceholder")}
      placeholder={t("daycareHotel:petForm.relationshipPetPlaceholder")}
      emptyMessage={isFetching ? t("common:loading") : t("common:noResults")}
      listClassName="max-h-[260px]"
      optionClassName="group flex items-center gap-3 py-2"
      renderOption={(option) => <PetOptionRow option={option as DaycareHotelPetOption} />}
    />
  );
}

export function PetDaycareHotelFields({ petId, value, onChange, disabled }: PetDaycareHotelFieldsProps) {
  const { t } = useTranslation("daycareHotel");
  const { mode } = useDaycareHotelAccess();
  const { data: initialDraft } = usePetDaycareHotelInitialDraft(mode === "none" ? null : petId);
  const seededPetId = useRef<string | null>(null);

  const relatedPetIds = value.relationships
    .filter((relationship) => !relationship.removed && relationship.relatedPetId)
    .map((relationship) => relationship.relatedPetId);
  const { data: relatedPetsById } = useDaycareHotelPetsByIds(relatedPetIds);
  const { data: activityTypes = [] } = useStayActivityTypes();
  const { data: packs = [], isLoading: isPacksLoading } = useStayPacks();

  useEffect(() => {
    if (!petId || !initialDraft || seededPetId.current === petId) return;
    seededPetId.current = petId;
    onChange(initialDraft);
  }, [petId, initialDraft, onChange]);

  if (mode === "none") return null;

  const patch = (changes: Partial<PetDaycareHotelDraft>) => onChange({ ...value, ...changes });
  const visible = <T extends { removed?: boolean }>(rows: T[]) => rows.filter((row) => !row.removed);
  const markRemoved = <T extends { id: string; removed?: boolean }>(rows: T[], id: string) =>
    rows.map((row) => (row.id === id ? { ...row, removed: true } : row));

  const visibleAlerts = visible(value.alerts);
  const visibleItems = visible(value.storedItems);
  const visibleRelationships = visible(value.relationships);
  const visibleRecurringActivities = visible(value.recurringActivities);

  return (
    <Card className="border border-border !shadow-none">
      <CardHeader>
        <CardTitle className="text-base">{t("petForm.title")}</CardTitle>
        <CardDescription>{t("petForm.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <SectionHeader
            icon={Users}
            title={t("petForm.packsLabel")}
            description={t("petForm.packsDescription")}
          />
          {isPacksLoading ? (
            <p className="text-sm text-muted-foreground">{t("petForm.packsLoading")}</p>
          ) : packs.length === 0 ? (
            <EmptyState>{t("petForm.packsEmpty")}</EmptyState>
          ) : (
            <MultipleAutocomplete
              options={packs.map((pack) => ({ value: pack.id, label: pack.name }))}
              values={value.packIds}
              onChange={(packIds) => patch({ packIds })}
              placeholder={t("petForm.packsSelect")}
              emptyMessage={t("petForm.packsEmpty")}
              removeLabel={t("petForm.packsRemove")}
              disabled={disabled}
              popoverPortalled={false}
              optionClassName="group flex items-center gap-2 py-2"
              ariaLabel={t("petForm.packsSelect")}
              renderOption={(option, selected) => (
                <>
                  <span
                    aria-hidden="true"
                    className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colorDotClass(packs.find((pack) => pack.id === option.value)?.color ?? null))}
                    style={colorDotStyle(packs.find((pack) => pack.id === option.value)?.color ?? null)}
                  />
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {selected ? <span aria-hidden="true" className="text-primary">✓</span> : null}
                </>
              )}
            />
          )}
        </div>

        <div className="space-y-2">
          <SectionHeader
            icon={AlertTriangle}
            title={t("petForm.alertsLabel")}
            description={t("petForm.alertsDescription")}
          />
          {visibleAlerts.length === 0 ? (
            <EmptyState>{t("petForm.alertsEmpty")}</EmptyState>
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
              {visibleAlerts.map((alert) => {
                const presentation = petAlertColorPresentation(alert.color);
                return (
                  <RowShell key={alert.id}>
                    <div className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className={cn("h-2.5 w-2.5 shrink-0 rounded-full", presentation.dotClass)}
                        style={
                          presentation.customColor
                            ? { backgroundColor: presentation.customColor }
                            : undefined
                        }
                      />
                      <Input
                        aria-label={t("petForm.alertPlaceholder")}
                        placeholder={t("petForm.alertPlaceholder")}
                        className="min-w-0 flex-1"
                        disabled={disabled}
                        value={alert.value}
                        onChange={(event) =>
                          patch({
                            alerts: value.alerts.map((row) =>
                              row.id === alert.id ? { ...row, value: event.target.value } : row,
                            ),
                          })
                        }
                      />
                      <IconButton
                        type="button"
                        label={t("petForm.remove")}
                        className="shrink-0 text-muted-foreground hover:text-state-danger"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => patch({ alerts: markRemoved(value.alerts, alert.id) })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{t("petForm.alertColorLabel")}</span>
                      <AlertColorPicker
                        value={alert.color ?? ""}
                        label={t("petForm.alertColorLabel")}
                        onChange={(color) =>
                          patch({
                            alerts: value.alerts.map((row) =>
                              row.id === alert.id ? { ...row, color } : row,
                            ),
                          })
                        }
                      />
                    </div>
                  </RowShell>
                );
              })}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() =>
              patch({
                alerts: [
                  ...value.alerts,
                  { id: draftTempId(), label: t("petForm.alertsLabel"), value: "", color: "orange" },
                ],
              })
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("petForm.addAlert")}
          </Button>
        </div>

        <div className="space-y-3 border-t border-border pt-5">
          <SectionHeader
            icon={Package}
            title={t("petForm.storedItemsLabel")}
            description={t("petForm.storedItemsDescription")}
          />
          {visibleItems.length === 0 ? (
            <EmptyState>{t("petForm.storedItemsEmpty")}</EmptyState>
          ) : (
            <div className="space-y-2">
              {visibleItems.map((item) => (
                <RowShell key={item.id}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Label
                        htmlFor={`stored-item-name-${item.id}`}
                        className="text-xs font-normal text-muted-foreground"
                      >
                        {t("petForm.itemNamePlaceholder")}
                      </Label>
                      <Input
                        id={`stored-item-name-${item.id}`}
                        aria-label={t("petForm.itemNamePlaceholder")}
                        placeholder={t("petForm.itemNamePlaceholder")}
                        disabled={disabled}
                        value={item.itemName}
                        onChange={(event) =>
                          patch({
                            storedItems: value.storedItems.map((row) =>
                              row.id === item.id ? { ...row, itemName: event.target.value } : row,
                            ),
                          })
                        }
                      />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Label
                        htmlFor={`stored-item-location-${item.id}`}
                        className="text-xs font-normal text-muted-foreground"
                      >
                        {t("petForm.itemLocationPlaceholder")}
                      </Label>
                      <Input
                        id={`stored-item-location-${item.id}`}
                        aria-label={t("petForm.itemLocationPlaceholder")}
                        placeholder={t("petForm.itemLocationPlaceholder")}
                        disabled={disabled}
                        value={item.storageLocation ?? ""}
                        onChange={(event) =>
                          patch({
                            storedItems: value.storedItems.map((row) =>
                              row.id === item.id ? { ...row, storageLocation: event.target.value } : row,
                            ),
                          })
                        }
                      />
                    </div>
                    <IconButton
                      type="button"
                      label={t("petForm.remove")}
                      disabled={disabled}
                      className="self-end"
                      onClick={() => patch({ storedItems: markRemoved(value.storedItems, item.id) })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </IconButton>
                  </div>
                </RowShell>
              ))}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() =>
              patch({
                storedItems: [
                  ...value.storedItems,
                  { id: draftTempId(), itemName: "", storageLocation: null },
                ],
              })
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("petForm.addStoredItem")}
          </Button>
        </div>

        <div className="space-y-3 border-t border-border pt-5">
          <SectionHeader
            icon={ListChecks}
            title={t("petForm.recurringActivitiesLabel")}
            description={t("petForm.recurringActivitiesDescription")}
          />
          {visibleRecurringActivities.length === 0 ? (
            <EmptyState>{t("petForm.recurringActivitiesEmpty")}</EmptyState>
          ) : (
            <div className="space-y-2">
              {visibleRecurringActivities.map((activity) => (
                <RowShell key={activity.id}>
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
                    <div className="space-y-1.5">
                      <Label htmlFor={`recurring-activity-type-${activity.id}`} className="text-xs font-normal text-muted-foreground">
                        {t("petForm.recurringActivityTypeLabel")}
                      </Label>
                      <Select
                        disabled={disabled}
                        value={activity.typeId}
                        onValueChange={(typeId) => {
                          const type = activityTypes.find((candidate) => candidate.id === typeId);
                          patch({
                            recurringActivities: value.recurringActivities.map((row) =>
                              row.id === activity.id
                                ? {
                                    ...row,
                                    typeId,
                                    typeName: type?.name ?? row.typeName,
                                    typeColor: type?.color ?? row.typeColor,
                                    typeIcon: type?.icon ?? row.typeIcon,
                                  }
                                : row,
                            ),
                          });
                        }}
                      >
                        <SelectTrigger id={`recurring-activity-type-${activity.id}`} aria-label={t("petForm.recurringActivityTypeLabel")}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {activityTypes.map((type) => <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`recurring-activity-description-${activity.id}`} className="text-xs font-normal text-muted-foreground">
                        {t("petForm.recurringActivityDescriptionLabel")}
                      </Label>
                      <Input
                        id={`recurring-activity-description-${activity.id}`}
                        aria-label={t("petForm.recurringActivityDescriptionLabel")}
                        placeholder={t("petForm.recurringActivityDescriptionLabel")}
                        disabled={disabled}
                        value={activity.description}
                        onChange={(event) => patch({
                          recurringActivities: value.recurringActivities.map((row) => row.id === activity.id ? { ...row, description: event.target.value } : row),
                        })}
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="min-w-28 space-y-1.5">
                        <Label htmlFor={`recurring-activity-time-${activity.id}`} className="text-xs font-normal text-muted-foreground">
                          {t("petForm.recurringActivityTimeLabel")}
                        </Label>
                        <div className="relative">
                          <Clock3 aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id={`recurring-activity-time-${activity.id}`}
                            aria-label={t("petForm.recurringActivityTimeLabel")}
                            type="time"
                            className="pl-9"
                            disabled={disabled}
                            value={activity.scheduledTime ?? ""}
                            onChange={(event) => patch({
                              recurringActivities: value.recurringActivities.map((row) => row.id === activity.id ? { ...row, scheduledTime: event.target.value || null } : row),
                            })}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pb-1">
                        <Switch
                          id={`recurring-activity-${activity.id}`}
                          checked={activity.recurring}
                          disabled={disabled}
                          onCheckedChange={(recurring) => patch({
                            recurringActivities: value.recurringActivities.map((row) => row.id === activity.id ? { ...row, recurring } : row),
                          })}
                        />
                        <Label htmlFor={`recurring-activity-${activity.id}`} className="text-xs text-muted-foreground">
                          {t("petForm.recurringActivityLabel")}
                        </Label>
                      </div>
                      <IconButton
                        type="button"
                        label={t("petForm.remove")}
                        disabled={disabled}
                        className="mb-0.5 shrink-0"
                        onClick={() => patch({ recurringActivities: markRemoved(value.recurringActivities, activity.id) })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    </div>
                  </div>
                </RowShell>
              ))}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || activityTypes.length === 0}
            onClick={() => {
              const type = activityTypes[0];
              if (!type) return;
              patch({
                recurringActivities: [
                  ...value.recurringActivities,
                  {
                    id: draftTempId(),
                    typeId: type.id,
                    typeName: type.name,
                    typeColor: type.color,
                    typeIcon: type.icon,
                    description: "",
                    scheduledTime: null,
                    recurring: true,
                  },
                ],
              });
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("petForm.addRecurringActivity")}
          </Button>
        </div>

        <div className="space-y-3 border-t border-border pt-5">
          <SectionHeader
            icon={Heart}
            title={t("petForm.relationshipsLabel")}
            description={t("petForm.relationshipsDescription")}
          />
          {visibleRelationships.length === 0 ? (
            <EmptyState>{t("petForm.relationshipsEmpty")}</EmptyState>
          ) : (
            <div className="space-y-2">
              {visibleRelationships.map((relationship) => (
                <RowShell key={relationship.id}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Label className="text-xs font-normal text-muted-foreground">
                        {t("petForm.relationshipPetPlaceholder")}
                      </Label>
                      <RelationshipPetPicker
                        petId={petId}
                        disabled={disabled}
                        selectedPet={relatedPetsById?.[relationship.relatedPetId] ?? null}
                        onSelect={(option) =>
                          patch({
                            relationships: value.relationships.map((row) =>
                              row.id === relationship.id ? { ...row, relatedPetId: option.petId } : row,
                            ),
                          })
                        }
                      />
                    </div>
                    <div className="flex items-end gap-2 sm:w-56">
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <Label className="text-xs font-normal text-muted-foreground">
                          {t("petForm.relationshipKindLabel")}
                        </Label>
                        <Select
                          disabled={disabled}
                          value={relationship.relationship}
                          onValueChange={(kind) =>
                            patch({
                              relationships: value.relationships.map((row) =>
                                row.id === relationship.id
                                  ? { ...row, relationship: kind as PetRelationshipKind }
                                  : row,
                              ),
                            })
                          }
                        >
                          <SelectTrigger aria-label={t("petForm.relationshipKindLabel")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RELATIONSHIP_KINDS.map((kind) => (
                              <SelectItem key={kind} value={kind}>
                                <span className="flex items-center gap-2">
                                  <span
                                    className={cn("h-2 w-2 rounded-full", RELATIONSHIP_DOT_CLASS[kind])}
                                    aria-hidden="true"
                                  />
                                  {t(`petForm.relationshipKind.${kind}`)}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <IconButton
                        type="button"
                        label={t("petForm.remove")}
                        disabled={disabled}
                        onClick={() =>
                          patch({ relationships: markRemoved(value.relationships, relationship.id) })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    </div>
                  </div>
                </RowShell>
              ))}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() =>
              patch({
                relationships: [
                  ...value.relationships,
                  { id: draftTempId(), relatedPetId: "", relationship: "likes" },
                ],
              })
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("petForm.addRelationship")}
          </Button>
        </div>

        <div className="space-y-3 border-t border-border pt-5">
          <Label htmlFor="daycare-hotel-notes">
            {t(getPetStayNotesLabelKey(mode), { defaultValue: t("petForm.notesLabel") })}
          </Label>
          <Textarea
            id="daycare-hotel-notes"
            aria-label={t("petForm.notesLabel")}
            rows={3}
            className="resize-none"
            disabled={disabled}
            placeholder={t("petForm.notesPlaceholder")}
            value={value.notes}
            onChange={(event) => patch({ notes: event.target.value })}
          />
        </div>
      </CardContent>
    </Card>
  );
}
